// src/hooks/use-text-zoom.ts
'use client'

// Session-persisted text zoom + the large-print accommodation (#784 / #830).
//
// Extracted from Runner.tsx so the practice surfaces get IDENTICAL behaviour
// rather than a second, slightly-different copy. The subtle part worth sharing
// is the precedence rule: large print auto-jumps to the largest level, but only
// when the student hasn't already chosen a zoom this session. Their explicit
// choice always wins — an accommodation is a better default, not an override.
//
// The storage key is shared with the runner on purpose: a student who sets
// their size on a mock test keeps it in practice, which is what "persists for
// the session" should mean across the app.

import { useCallback, useEffect, useRef, useState } from 'react'
import { ZOOM_LEVELS } from '@/components/sat/TextZoom'

export const ZOOM_STORAGE_KEY = 'sat-zoom'
const MAX_ZOOM = ZOOM_LEVELS[ZOOM_LEVELS.length - 1]

function readStoredZoom(): number | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(ZOOM_STORAGE_KEY)
    if (raw == null) return null
    const v = parseFloat(raw)
    return (ZOOM_LEVELS as readonly number[]).includes(v) ? v : null
  } catch {
    // Private-mode / blocked storage: behave as "nothing stored".
    return null
  }
}

export type UseTextZoom = {
  /** Current level, applied by the caller as CSS `zoom` on the content root. */
  level: number
  /** Set + persist a level (wire to <TextZoom onChange>). */
  setLevel: (level: number) => void
}

/**
 * @param largePrint the user's large-print accommodation flag. Passing it
 *   `false` while it is still loading is safe — the effect re-runs and applies
 *   the jump once it flips true, and it never overwrites a stored choice.
 */
export function useTextZoom(largePrint?: boolean): UseTextZoom {
  const [level, setLevelState] = useState<number>(() => readStoredZoom() ?? 1)
  // The auto-jump is a one-shot: without this guard, a student on large print
  // who deliberately zooms back DOWN would be yanked to max on the next render.
  const autoAppliedRef = useRef(false)

  const setLevel = useCallback((next: number) => {
    setLevelState(next)
    try {
      window.sessionStorage.setItem(ZOOM_STORAGE_KEY, String(next))
    } catch {
      // ignore — the in-memory level still applies for this screen
    }
  }, [])

  useEffect(() => {
    if (!largePrint || autoAppliedRef.current) return
    autoAppliedRef.current = true
    // Only default up when the student has expressed no preference.
    if (readStoredZoom() == null) setLevelState(MAX_ZOOM)
  }, [largePrint])

  return { level, setLevel }
}
